import {
	Action,
	type Customer,
	type ScheduleSessionAttendanceStatus,
} from "@pilatestime/shared-types";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Ban, Eye, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchAttendanceBySessionQueryOptions } from "@/api/scheduleSessionAttendance";
import { fetchScheduleSessionQueryOptions } from "@/api/schedules";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	useBulkCreateAttendance,
	useUpdateAttendanceStatus,
} from "@/hooks/useAttendance";
import { useScheduleLabel } from "@/hooks/useScheduleLabel";
import {
	useCancelScheduleSession,
	useReactivateScheduleSession,
	useRescheduleScheduleSession,
} from "@/hooks/useScheduleSessions";
import { nowInSessionFrame } from "@/routes/_auth/app/_app/org/$orgId/-components/dashboard/sessionStatus";
import { useLiveClock } from "@/routes/_auth/app/_app/org/$orgId/-components/dashboard/useLiveClock";
import { AddCustomerModal } from "@/routes/_auth/app/_app/org/$orgId/attendance/-components/AddCustomerModal";
import { getAttendanceStatus } from "../-components/mobile/attendanceMobileUtils";
import { SessionDetailsSkeleton } from "./-components/SessionDetailsSkeleton";
import {
	CancelSessionDialog,
	ReactivateSessionDialog,
	RescheduleSessionDialog,
} from "./-components/SessionLifecycleDialogs";
import { SessionRoster } from "./-components/SessionRoster";
import { SessionSummaryCard } from "./-components/SessionSummaryCard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/attendance/session/$sessionId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, sessionId } = params;

		// Preload session data
		context.queryClient.ensureQueryData(
			fetchScheduleSessionQueryOptions(orgId, sessionId),
		);

		// Preload attendance data
		context.queryClient.ensureQueryData(
			fetchAttendanceBySessionQueryOptions(token, orgId, sessionId, userId),
		);

		return { userId, token, orgId, sessionId };
	},
	pendingComponent: SessionDetailsSkeleton,
	component: SessionDetailsPage,
});

function SessionDetailsPage() {
	const { t } = useTranslation();
	const formatScheduleLabel = useScheduleLabel();
	const { token, orgId, sessionId, userId } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const checkCan = useCan();
	const canManageAttendance = checkCan(Action.MANAGE_ATTENDANCE);

	// Modal state
	const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [isReactivateModalOpen, setIsReactivateModalOpen] = useState(false);
	const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);

	// Mutations
	const bulkCreateAttendanceMutation = useBulkCreateAttendance({
		error: t("attendance.addCustomer.errors.failed", { error: "{{error}}" }),
	});

	const updateAttendanceStatusMutation = useUpdateAttendanceStatus({
		success: t("attendance.statusUpdate.success"),
		error: t("attendance.statusUpdate.error"),
	});

	const cancelSessionMutation = useCancelScheduleSession(orgId);
	const reactivateSessionMutation = useReactivateScheduleSession(orgId);
	const rescheduleSessionMutation = useRescheduleScheduleSession(orgId);

	// Get preloaded data using useSuspenseQuery
	const { data: session } = useSuspenseQuery(
		fetchScheduleSessionQueryOptions(orgId, sessionId),
	);

	const { data: attendances } = useSuspenseQuery(
		fetchAttendanceBySessionQueryOptions(token, orgId, sessionId, userId),
	);

	// Ensure attendances is always an array
	const safeAttendances = attendances || [];

	// Live lifecycle status (upcoming / in progress / done / canceled), sharing
	// the calendar surfaces' derivation so both read the same.
	const liveNow = useLiveClock(30_000);
	const now = useMemo(() => nowInSessionFrame(liveNow), [liveNow]);
	const sessionStatus = getAttendanceStatus(session, now).kind;

	const handleStatusChange = (
		customerId: string,
		newStatus: ScheduleSessionAttendanceStatus,
	) => {
		updateAttendanceStatusMutation.mutate({
			token,
			organizationId: orgId,
			sessionId,
			customerId,
			userId,
			status: newStatus,
		});
	};

	// Handle adding customers to session
	const handleAddCustomers = async (
		customersWithStatus: {
			customer: Customer;
			status: ScheduleSessionAttendanceStatus;
		}[],
	) => {
		if (customersWithStatus.length === 0) {
			toast.error(t("attendance.addCustomer.errors.noCustomersSelected"));
			return;
		}

		const customersForAPI = customersWithStatus.map((item) => ({
			customerId: item.customer.id,
			status: item.status,
		}));

		const customerNames = customersWithStatus
			.map((item) => `${item.customer.firstName} ${item.customer.lastName}`)
			.join(", ");

		bulkCreateAttendanceMutation.mutate(
			{
				token,
				organizationId: orgId,
				sessionId,
				userId,
				customersWithStatus: customersForAPI,
			},
			{
				onSuccess: () => {
					toast.success(
						t("attendance.addCustomer.success", {
							count: customersWithStatus.length,
							customers: customerNames,
						}),
					);
					setIsAddCustomerModalOpen(false);
				},
			},
		);
	};

	const handleCancelSession = () => {
		cancelSessionMutation.mutate(sessionId, {
			onSuccess: () => {
				toast.success(t("attendance.sessionDetails.cancelSession.success"));
				setIsCancelModalOpen(false);
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions"],
				});
				navigate({ to: "/app/org/$orgId/attendance", params: { orgId } });
			},
			onError: () => {
				toast.error(t("attendance.sessionDetails.cancelSession.error"));
			},
		});
	};

	const handleReactivateSession = () => {
		reactivateSessionMutation.mutate(sessionId, {
			onSuccess: () => {
				toast.success(t("attendance.sessionDetails.reactivateSession.success"));
				setIsReactivateModalOpen(false);
				queryClient.invalidateQueries({
					queryKey: ["scheduleSessions"],
				});
				queryClient.invalidateQueries({
					queryKey: ["scheduleSession", orgId, sessionId],
				});
			},
			onError: () => {
				toast.error(t("attendance.sessionDetails.reactivateSession.error"));
			},
		});
	};

	const handleOpenRescheduleModal = () => {
		setIsCancelModalOpen(false);
		setIsRescheduleModalOpen(true);
	};

	const handleRescheduleSession = (values: {
		date: string;
		startTime: string;
		endTime: string;
	}) => {
		// Parse the date and convert to epoch timestamp (UTC midnight)
		const [year, month, day] = values.date.split("-").map(Number);
		const newSessionDate = Date.UTC(year, month - 1, day);

		rescheduleSessionMutation.mutate(
			{
				sessionId,
				newSessionDate,
				newStartTime: values.startTime,
				newEndTime: values.endTime,
			},
			{
				onSuccess: (result) => {
					toast.success(
						t("attendance.sessionDetails.rescheduleSession.success"),
					);
					setIsRescheduleModalOpen(false);
					queryClient.invalidateQueries({
						queryKey: ["scheduleSessions"],
					});
					navigate({
						to: "/app/org/$orgId/attendance/session/$sessionId",
						params: { orgId, sessionId: result.newSession.id },
					});
				},
				onError: () => {
					toast.error(t("attendance.sessionDetails.rescheduleSession.error"));
				},
			},
		);
	};

	return (
		<div className="bg-base-200 pb-8">
			{/* Header. The org shell's <main> scrollport carries pt-2/md:p-4, so the
			    sticky offset is pulled negative to pin flush with the visual top. */}
			<div className="sticky -top-2 z-10 border-b border-base-300 bg-base-100 md:-top-4">
				<div className="mx-auto max-w-5xl px-4 py-3 md:py-4">
					<PageHeader
						title={formatScheduleLabel(session.labelInputs)}
						description={t("attendance.sessionDetails.title")}
						showBackButton
						backButtonText={t("common.back")}
						actions={
							<div className="flex items-center gap-2">
								<Button variant="outline" size="sm" asChild>
									<Link
										to="/app/org/$orgId/schedules/$scheduleId"
										params={{ orgId, scheduleId: session.scheduleId }}
									>
										<Eye aria-hidden="true" />
										<span className="hidden md:inline">
											{t("attendance.sessionDetails.viewSchedule")}
										</span>
									</Link>
								</Button>
								{canManageAttendance && !session.isCanceled && (
									<Button
										variant="outline"
										size="sm"
										onClick={() => setIsCancelModalOpen(true)}
										className="border-error/40 text-error hover:bg-error/10 hover:text-error"
									>
										<Ban aria-hidden="true" />
										<span className="hidden md:inline">
											{t("attendance.sessionDetails.cancelSession.button")}
										</span>
									</Button>
								)}
							</div>
						}
					/>
				</div>
			</div>

			{/* Main content */}
			<div className="mx-auto max-w-5xl space-y-4 px-4 py-4 md:space-y-6 md:py-6">
				{session.isCanceled && (
					<div className="flex flex-wrap items-center gap-3 rounded-lg border border-error/20 bg-error/10 px-4 py-3">
						<AlertTriangle
							className="h-4 w-4 shrink-0 text-error"
							aria-hidden="true"
						/>
						<p className="min-w-0 flex-1 text-sm font-medium text-error">
							{t("attendance.sessionDetails.sessionCanceled")}
						</p>
						{canManageAttendance && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => setIsReactivateModalOpen(true)}
								className="border-success/40 text-success hover:bg-success/10 hover:text-success"
							>
								<RefreshCw aria-hidden="true" />
								{t("attendance.sessionDetails.reactivateSession.button")}
							</Button>
						)}
					</div>
				)}

				<SessionSummaryCard session={session} status={sessionStatus} />

				<SessionRoster
					attendances={safeAttendances}
					canManage={canManageAttendance}
					isUpdating={updateAttendanceStatusMutation.isPending}
					onStatusChange={handleStatusChange}
					onAddCustomer={() => setIsAddCustomerModalOpen(true)}
				/>
			</div>

			{/* Lifecycle dialogs */}
			<CancelSessionDialog
				open={isCancelModalOpen}
				onOpenChange={setIsCancelModalOpen}
				onConfirm={handleCancelSession}
				onReschedule={handleOpenRescheduleModal}
				isPending={cancelSessionMutation.isPending}
			/>
			<ReactivateSessionDialog
				open={isReactivateModalOpen}
				onOpenChange={setIsReactivateModalOpen}
				onConfirm={handleReactivateSession}
				isPending={reactivateSessionMutation.isPending}
			/>
			<RescheduleSessionDialog
				open={isRescheduleModalOpen}
				onOpenChange={setIsRescheduleModalOpen}
				session={session}
				onConfirm={handleRescheduleSession}
				isPending={rescheduleSessionMutation.isPending}
			/>

			{/* Add Customer Modal */}
			<AddCustomerModal
				isOpen={isAddCustomerModalOpen}
				onClose={() => setIsAddCustomerModalOpen(false)}
				onAddCustomers={handleAddCustomers}
				organizationId={orgId}
				token={token}
				userId={userId}
			/>
		</div>
	);
}
